import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../utils/firebase';
import { getEntitlementsSummary } from '../utils/entitlementsApi';
import { createCreatorCheckout } from '../utils/stripeClient';
import RevenueTracker from '../components/RevenueTracker';

const CreatorDashboard = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    const loadEntitlements = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const data = await getEntitlementsSummary();
        setEntitlements(data);
      } catch (error) {
        console.error('Error loading entitlements:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEntitlements();
  }, [user]);

  const handleUpgrade = async (plan) => {
    try {
      setUpgrading(true);
      const checkoutUrl = await createCreatorCheckout(plan);
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start upgrade process. Please try again.');
      setUpgrading(false);
    }
  };

  const getPlanDisplayName = (plan) => {
    switch (plan) {
      case 'none': return 'Free';
      case 'lite': return 'Creator Lite';
      case 'pro': return 'Creator Pro';
      default: return 'Unknown';
    }
  };

  const getPlanDescription = (plan) => {
    switch (plan) {
      case 'none': 
        return 'Build and preview games for free. Subscribe to host live games and submit to marketplace.';
      case 'lite': 
        return '4 launches per month, 4 marketplace submissions. Perfect for getting started.';
      case 'pro': 
        return '8 launches per month, unlimited marketplace submissions. For serious creators.';
      default: 
        return '';
    }
  };

  const getPlanPrice = (plan) => {
    switch (plan) {
      case 'lite': return '$60/month';
      case 'pro': return '$120/month';
      default: return 'Free';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading your dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Creator Dashboard</h1>
              <p className="text-gray-400 mt-2">Manage your subscription and creator tools</p>
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={() => navigate('/my-games')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                My Games
              </button>
              
              <button
                onClick={() => navigate('/add-game')}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Create New Game
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Current Plan Status */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">Current Plan</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="text-3xl font-bold text-white mb-2">
                {getPlanDisplayName(entitlements?.plan)}
              </div>
              <div className="text-gray-400 text-sm mb-2">
                {getPlanPrice(entitlements?.plan)}
              </div>
              <div className="text-gray-300 text-sm">
                {getPlanDescription(entitlements?.plan)}
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <div className="text-3xl font-bold text-white mb-2">
                {entitlements?.availableLaunches || 0}
              </div>
              <div className="text-gray-400 text-sm mb-2">Launches Available</div>
              <div className="text-gray-300 text-sm">
                {entitlements?.plan === 'none' 
                  ? 'Subscribe to get launches'
                  : `${entitlements?.usedLaunches || 0} used this month`
                }
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-4">
              <div className="text-3xl font-bold text-white mb-2">
                {entitlements?.usedSubmissions || 0}/{entitlements?.includedSubmissions === 'infinity' ? '∞' : entitlements?.includedSubmissions || 0}
              </div>
              <div className="text-gray-400 text-sm mb-2">Marketplace Submissions</div>
              <div className="text-gray-300 text-sm">
                {entitlements?.plan === 'none' 
                  ? 'Subscribe to submit games'
                  : entitlements?.includedSubmissions === 'infinity'
                    ? 'Unlimited submissions'
                    : 'This month'
                }
              </div>
            </div>
          </div>
        </div>

        {/* Plan Comparison */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Choose Your Plan</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Free Plan */}
            <div className={`bg-gray-700 rounded-lg p-6 border-2 ${
              entitlements?.plan === 'none' ? 'border-blue-500' : 'border-gray-600'
            }`}>
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">Free</h3>
                <div className="text-3xl font-bold text-white mb-4">$0</div>
                
                <ul className="text-gray-300 text-sm space-y-2 mb-6">
                  <li>✓ Build unlimited games</li>
                  <li>✓ Preview games</li>
                  <li>✗ Host live games</li>
                  <li>✗ Submit to marketplace</li>
                </ul>

                <div className="text-gray-400 text-sm">
                  {entitlements?.plan === 'none' ? 'Current Plan' : 'Basic Access'}
                </div>
              </div>
            </div>

            {/* Creator Lite */}
            <div className={`bg-gray-700 rounded-lg p-6 border-2 ${
              entitlements?.plan === 'lite' ? 'border-blue-500' : 'border-gray-600'
            }`}>
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">Creator Lite</h3>
                <div className="text-3xl font-bold text-white mb-4">$60<span className="text-lg">/month</span></div>
                
                <ul className="text-gray-300 text-sm space-y-2 mb-6">
                  <li>✓ Build unlimited games</li>
                  <li>✓ Preview games</li>
                  <li>✓ 4 launches per month</li>
                  <li>✓ 4 marketplace submissions</li>
                  <li>✓ $10 Launch Rights</li>
                </ul>

                {entitlements?.plan === 'lite' ? (
                  <div className="text-blue-400 text-sm font-semibold">Current Plan</div>
                ) : (
                  <button
                    onClick={() => handleUpgrade('lite')}
                    disabled={upgrading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {upgrading ? 'Processing...' : 'Upgrade to Lite'}
                  </button>
                )}
              </div>
            </div>

            {/* Creator Pro */}
            <div className={`bg-gray-700 rounded-lg p-6 border-2 ${
              entitlements?.plan === 'pro' ? 'border-blue-500' : 'border-gray-600'
            }`}>
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">Creator Pro</h3>
                <div className="text-3xl font-bold text-white mb-4">$120<span className="text-lg">/month</span></div>
                
                <ul className="text-gray-300 text-sm space-y-2 mb-6">
                  <li>✓ Build unlimited games</li>
                  <li>✓ Preview games</li>
                  <li>✓ 8 launches per month</li>
                  <li>✓ Unlimited marketplace submissions</li>
                  <li>✓ $10 Launch Rights</li>
                  <li>✓ Priority support</li>
                </ul>

                {entitlements?.plan === 'pro' ? (
                  <div className="text-blue-400 text-sm font-semibold">Current Plan</div>
                ) : (
                  <button
                    onClick={() => handleUpgrade('pro')}
                    disabled={upgrading}
                    className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {upgrading ? 'Processing...' : 'Upgrade to Pro'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Tracker */}
        <RevenueTracker />

        {/* Quick Actions */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-white mb-4">Quick Actions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/add-game')}
              className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition-colors text-center"
            >
              <div className="text-2xl mb-2">🎮</div>
              <div className="font-semibold">Create Game</div>
              <div className="text-sm text-blue-200">Build a new trivia game</div>
            </button>

            <button
              onClick={() => navigate('/my-games')}
              className="bg-gray-600 text-white p-4 rounded-lg hover:bg-gray-500 transition-colors text-center"
            >
              <div className="text-2xl mb-2">📋</div>
              <div className="font-semibold">My Games</div>
              <div className="text-sm text-gray-300">Manage your games</div>
            </button>

            <button
              onClick={() => navigate('/marketplace')}
              className="bg-green-600 text-white p-4 rounded-lg hover:bg-green-700 transition-colors text-center"
            >
              <div className="text-2xl mb-2">🏪</div>
              <div className="font-semibold">Marketplace</div>
              <div className="text-sm text-green-200">Browse games</div>
            </button>

            <button
              onClick={() => navigate('/account-settings')}
              className="bg-purple-600 text-white p-4 rounded-lg hover:bg-purple-700 transition-colors text-center"
            >
              <div className="text-2xl mb-2">⚙️</div>
              <div className="font-semibold">Settings</div>
              <div className="text-sm text-purple-200">Manage account</div>
            </button>
          </div>
        </div>

        {/* Billing Cycle Info */}
        {entitlements?.plan !== 'none' && (
          <div className="bg-gray-800 rounded-lg p-6 mt-8">
            <h2 className="text-2xl font-bold text-white mb-4">Billing Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-gray-400 text-sm mb-1">Current Cycle</div>
                <div className="text-white">
                  {entitlements?.cycleStart ? new Date(entitlements.cycleStart).toLocaleDateString() : 'N/A'} - {entitlements?.cycleEnd ? new Date(entitlements.cycleEnd).toLocaleDateString() : 'N/A'}
                </div>
              </div>
              
              <div>
                <div className="text-gray-400 text-sm mb-1">Next Reset</div>
                <div className="text-white">
                  {entitlements?.cycleEnd ? new Date(entitlements.cycleEnd).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorDashboard; 